/* eslint-disable quote-props */
import React from 'react';
import { toast } from 'react-toastify';
import { encode } from 'querystring';
import { Deserializer } from 'jsonapi-serializer';
import I18n from '~/i18n';
import Storage from '~/providers/Storage';
import { URL, PATH_LOGIN, PATH_REFRESH_TOKEN } from '~/config/uri';
// import { USERS_DOWNLOAD_APPOINTMENTS } from '~/config/constants';
import isBlank from '~/utils/isBlank';
import jwtDecode from 'jwt-decode';

const { deserialize } = new Deserializer({
  keyForAttribute: 'snake_case',
});

const NOT_TRANSLATE_VALIDATIONS = ['min'];

function Msg({ error, controller }) {
  return (
    <div>
      <h4>
        {error.length > 1
          ? I18n.t('form-toast-errors-title')
          : I18n.t('form-toast-error-title')}
      </h4>

      {error.map((item, index) =>
        !isBlank(item.source) && item.source.pointer !== 'required_email' ? (
          <li key={index}>
            {I18n.t(`${controller}.attributes.${item.source.pointer}`)}
            {item.title && !NOT_TRANSLATE_VALIDATIONS.includes(item.title)
              ? I18n.t(`${item.title}`)
              : `: ${item.detail}`}
          </li>
        ) : (
          <li key={index}>
            {item.detail || item.title || item.message || item.pack_type}
          </li>
        )
      )}
    </div>
  );
}

const toastfyListErrors = (e, controller) => {
  toast.error(<Msg error={e} controller={controller} />, {
    position: 'top-right',
    autoClose: false,
    hideProgressBar: false,
    closeOnClick: true,
    pauseOnHover: true,
    draggable: true,
    progress: undefined,
  });
};

export default class Request {
    static async login(body, setUser) {
    try {
      const response = await fetch(`${URL}${PATH_LOGIN}`, {
        method: 'POST',
        body: JSON.stringify(body),
        headers: await Request.getHeaders(),
      });

      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        console.info('catch response json', e);
      }

      if (!response.ok) {
        const error = new Error(
          data.error ? data.error.message : I18n.t('request-error')
        );
        if (data.errors) error.list = data.errors;

        error.status = response.status;
        throw error;
      }

      const { role, accepted_terms_at, countryId } = jwtDecode(data.token).data;

      if (
        !['admin', 'partner', 'doctor', 'attendant', 'marketing'].includes(role)
      ) {
        const error = new Error(I18n.t('adm:login-notadmin'));
        error.isPermissionError = true;
        throw error;
      }

      await Request.setAuth(data);
      const user = await Request.get('/admin/user_profile');
      await Storage.setItem(Storage.userKey, user.data);
      await Storage.setItem(Storage.acceptedTermsAt, accepted_terms_at);
      await Storage.setItem(Storage.countryId, countryId);

      setUser(user.data);

      toast.success(I18n.t('adm:login-success'));
      return data;
    } catch (e) {
      if (e.isPermissionError) {
        toast.error('Acesso negado: Você não possui permissão para acessar o painel administrativo. Entre em contato com o administrador do sistema.');
      } else if (e.list) {
        toastfyListErrors(e.list, {});
      } else {
        toast.error(e.message || I18n.t('request-error'));
      }
    }
  }

  static get(path, params = {}, config = {}) {
    return Request.request(
      `${URL}${path}`,
      {
        method: 'GET',
      },
      params,
      config
    );
  }

  static getNotDeserialize(path, params = {}, config = {}) {
    return Request.requestNotDeserialize(
      `${URL}${path}`,
      {
        method: 'GET',
      },
      params,
      config
    );
  }

  static async download(path, config = {}) {
    const urlWithParams = `${URL}${path}`;
    try {
      const response = await fetch(urlWithParams, {
        headers: await Request.getHeaders(config.authorizationToken),
      });
      if (!response.ok) {
        const error = new Error(response.statusText);
        error.status = response.status;
        throw error;
      }
      return response;
    } catch (e) {
      console.log(e);
    }
  }

  static post(path, data, params = {}, config = {}) {
    return Request.request(
      `${URL}${path}`,
      {
        method: 'POST',
        body: JSON.stringify(data),
      },
      params,
      config
    );
  }

  static put(path, data, params = {}, config = {}) {
    return Request.request(
      `${URL}${path}`,
      {
        method: 'PUT',
        body: JSON.stringify(data),
      },
      params,
      config
    );
  }

  static del(path, params = {}, config = {}) {
    return Request.request(
      `${URL}${path}`,
      {
        method: 'DELETE',
      },
      params,
      config
    );
  }

  static patch(path, data, params = {}, config = {}) {
    return Request.request(
      `${URL}${path}`,
      {
        method: 'PATCH',
        body: JSON.stringify(data),
      },
      params,
      config
    );
  }

  /* @private */
  static async refreshToken() {
    try {
      const refreshToken = await Storage.getItem(Storage.refreshTokenKey);
      const auth = await Request.post(
        PATH_REFRESH_TOKEN,
        { refreshToken },
        {},
        { normalizeJSON: false }
      );
      await Request.setAuth(auth);

      return true;
    } catch (e) {
      return false;
    }
  }

  /* @private */
  static async request(url, options, params, config = {}) {
    const urlWithParams = params ? `${url}?${encode(params)}` : url;

    try {
      const response = await fetch(urlWithParams, {
        ...options,
        headers: await Request.getHeaders(config.authorizationToken),
      });

      if (response.status === 401 && (await Request.refreshToken()))
        return await Request.request(url, options);

      if (response.status === 204) {
        return {};
      }

      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        console.info('catch response json', e);
      }

      if (!response.ok) {
        const error = new Error(
          data.error ? data.error.message : I18n.t('request-error')
        );
        if (data.errors) error.list = data.errors;

        error.status = response.status;
        throw error;
      }

      if (config.normalizeJSON === false) return data;

      let deserializeData = {};
      try {
        deserializeData = await deserialize(data);
      } catch (e) {
        console.info('catch deserialize', e);
      }

      return {
        meta: data.meta,
        data: deserializeData,
      };
    } catch (e) {
      if (config.disableToastErrors !== true) {
        if (e.list) {
          toastfyListErrors(e.list, config.controller);
        } else {
          toast.error(I18n.t('request-error'));
        }

        if (e.status) {
          if (e.status === 401) {
            toast.error('Credenciais inválidas');
          }
          // if(e.status === 403) {
          //   toast.error('Acesso negado');
          // }
          if (e.status === 404) {
            toast.error('A página requisitada não foi encontrada');
          }
          if (e.status === 500) {
            toast.error('Erro interno do servidor');
          }
        } else {
          toast.error(I18n.t('request-error'));
        }
      }

      throw e;
    }
  }

  /* @private */
  static async requestNotDeserialize(url, options, params, config = {}) {
    const urlWithParams = params ? `${url}?${encode(params)}` : url;

    try {
      const response = await fetch(urlWithParams, {
        ...options,
        headers: await Request.getHeaders(config.authorizationToken),
      });

      if (response.status === 401 && (await Request.refreshToken()))
        return await Request.request(url, options);

      if (response.status === 204) {
        return {};
      }

      let data = {};
      try {
        data = await response.json();
      } catch (e) {
        console.info('catch response json', e);
      }

      if (!response.ok) {
        const error = new Error(
          data.error ? data.error.message : I18n.t('request-error')
        );
        if (data.errors) error.list = data.errors;

        error.status = response.status;
        throw error;
      }

      if (config.normalizeJSON === false) return data;

      return {
        meta: data.meta,
        data: data.data,
      };
    } catch (e) {
      if (config.disableToastErrors !== true) {
        if (e.list) {
          toastfyListErrors(e.list, config.controller);
        } else {
          toast.error(I18n.t('request-error'));
        }

        if (e.status) {
          if (e.status === 401) {
            toast.error('Credenciais inválidas');
          }
          // if(e.status === 403) {
          //   toast.error('Acesso negado');
          // }
          if (e.status === 404) {
            toast.error('A página requisitada não foi encontrada');
          }
          if (e.status === 500) {
            toast.error('Erro interno do servidor');
          }
        } else {
          toast.error(I18n.t('request-error'));
        }
      }

      throw e;
    }
  }

  /* @private */
  static async getHeaders(authorizationToken) {
    if (isBlank(authorizationToken))
      authorizationToken = await Request.getAuth();
    return {
      Authorization: `Bearer ${authorizationToken}`,
      Accept: 'application/json',
      'Accept-Language': 'pt-BR',
      'Content-Type': 'application/json',
    };
  }

  /* @private */
  static async setAuth({ token, refreshToken }) {
    await Storage.setItem(Storage.tokenKey, token);
    await Storage.setItem(Storage.refreshTokenKey, refreshToken);
  }

  /* @private */
  static async getAuth() {
    return await Storage.getItem(Storage.tokenKey);
  }
}
